import React, { useRef, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Toast } from "primereact/toast";
import { zonedTimeToUtc, utcToZonedTime } from "date-fns-tz";
import { ActionColumnButtonWrapper } from "../styledComponents";
import { ShiftAdjustment, ShiftAdjustModal } from "../ShiftAdjust/ShiftAdjustModal";
import { PayrollStatusReason } from "../../../types/Payroll.types";
import { TimesheetsShiftInfo } from "../../../types/Shift.types";
import { getExternalUserId } from "../../../../../redux/selectors/auth";
import {
  getPayrollActionKeyPrefix,
  useDisputeTimekeepingMutation,
} from "../../../../../api/bluecrew/hooks/payroll";
import {
  buildShiftKey,
  buildShiftTimes,
  getDisputeReasonCodesWithShiftAdjustment,
} from "../../../shiftUtils";
import { ActionButton, ActionButtonIcon } from "./styledComponents";
import { ActionButtonIconPathPrefix } from "./index";
import { Tooltip } from "../../../../../components/Tooltip";
import { useAppSelector } from "../../../../../redux";

export type ButtonFlagProps = {
  rowData: TimesheetsShiftInfo;
  disabled?: boolean;
};

export const ButtonFlag = ({ rowData, disabled }: ButtonFlagProps) => {
  const [isDisputeVisible, setIsDisputeVisible] = useState<boolean>(false);

  const toast = useRef<Toast>(null);
  const showErrorToast = (message: string) => {
    toast.current?.show({ severity: "error", summary: "Error", detail: message });
  };

  const wpUserId = useAppSelector(getExternalUserId);

  const headerProps = {
    headerText: "Dispute Timesheet",
    subHeaderText: "All disputes are reviewed by the Crew Member and Bluecrew Support.",
  };
  const footerProps = {
    footerButtonLabel: "Dispute",
  };
  const adjustmentCodes = [
    PayrollStatusReason.SUPPORT_UNADJUSTED,
    PayrollStatusReason.NO_CALL_NO_SHOW,
    PayrollStatusReason.WALK_OFF,
    PayrollStatusReason.NO_LONGER_NEEDED,
    PayrollStatusReason.UNQUALIFIED_HOURS,
    PayrollStatusReason.UNQUALIFIED,
  ];
  const adjustmentCodesWithTimeEdit = getDisputeReasonCodesWithShiftAdjustment();
  const { user, job, userShift } = rowData;
  const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const disputeShiftRequest = {
    userId: user.externalId,
    jobId: job.externalId,
    shiftIndex: job.shiftIndex,
    userShift,
    shiftInfo: rowData,
  };

  const shiftKey = buildShiftKey(rowData);
  const tooltipAnchor = `excuse-${shiftKey.cmId}-${shiftKey.jobId}-${shiftKey.shiftIndex}`;
  const { mutate } = useDisputeTimekeepingMutation(shiftKey, showErrorToast);
  const isAnyPayrollActionMutating = !!useIsMutating(getPayrollActionKeyPrefix(shiftKey));
  const flagShiftHandler = (adjustment: ShiftAdjustment) => {
    const utcClockIn = adjustment.clockIn ? zonedTimeToUtc(adjustment.clockIn, job.timezone) : undefined;
    const clientClockIn = utcClockIn ? utcToZonedTime(utcClockIn, clientTz) : undefined;
    
    const utcClockOut = adjustment.clockOut ? zonedTimeToUtc(adjustment.clockOut, job.timezone) : undefined;
    const clientClockOut = utcClockOut ? utcToZonedTime(utcClockOut, clientTz) : undefined;

    const shiftBreaks = adjustment.breaks
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ? adjustment.breaks.filter(item => item.start && item.end).map((item) => ({
        baseStart: item.start,
        baseEnd: item.end,
      })).map((item) => ({
        start: utcToZonedTime(zonedTimeToUtc(item.baseStart, job.timezone), clientTz),
        end: utcToZonedTime(zonedTimeToUtc(item.baseEnd, job.timezone), clientTz),
      }))
      : undefined;

    return mutate({
      ...disputeShiftRequest,
      wpUserId,
      disputeInfoTimekeeping: {
        shiftStart: clientClockIn,
        shiftEnd: clientClockOut,
        breaks: shiftBreaks,
        reason: adjustment.reason,
      },
    });
  };

  return (
    <ActionColumnButtonWrapper>
      <Toast ref={toast} />
      <Tooltip position={"top"} target={`.${tooltipAnchor}`}>
        {"Flag"}
      </Tooltip>
      <ActionButton
        className={tooltipAnchor}
        disabled={disabled || isAnyPayrollActionMutating}
        icon={<ActionButtonIcon src={`${ActionButtonIconPathPrefix}/flag.svg`} />}
        rounded
        outlined
        onClick={() => {
          setIsDisputeVisible(true);
        }}
      />
      <ShiftAdjustModal
        headerProps={headerProps}
        footerProps={footerProps}
        shiftInfo={rowData}
        adjustmentCodes={adjustmentCodes}
        adjustmentCodesWithTimeEdit={adjustmentCodesWithTimeEdit}
        shiftAdjustModalVisible={isDisputeVisible}
        setShiftAdjustModalVisible={setIsDisputeVisible}
        shiftAdjustmentHandler={flagShiftHandler}
        defaultShiftTimes={buildShiftTimes(rowData)}
      />
    </ActionColumnButtonWrapper>
  );
};
